import React from 'react';
import { observer } from 'mobx-react-lite';
import { DocumentCard } from 'components/common/documents-upload/DocumentCard';
import DocumentDelegate from 'store/modules/upload-documents/documentDelegate';
import { OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import NoDataPlaceholder from 'components/common/general/NoDataPlaceholder';
import { DocumentStatus } from 'types/documentsService';
import {
  DocumentsSectionStyled,
  InactiveListCaptionStyled,
  InactiveListTitleStyled,
  InactiveSectionStyled,
  StyledListWrapper,
} from './styles';
import {
  ACTIVE_DOCUMENTS_LIST_TEST_ID,
  DOCUMENT_LIST_SPINNER_TEST_ID,
  INACTIVE_DOCUMENTS_LIST_TEST_ID,
  NO_DOCUMENTS_PLACEHOLDER_TEST_ID,
} from '../constants';

interface Props {
  activeDocuments: DocumentDelegate[];
  inactiveDocuments: DocumentDelegate[];
  loading: boolean;
  disableMutativeActions?: boolean;
}

export const DocumentList: React.FC<Props> = observer(
  ({ activeDocuments, inactiveDocuments, loading, disableMutativeActions = false }) => {
    const areDocumentsMissing = activeDocuments.length === 0;
    return (
      <DocumentsSectionStyled data-test-id={ACTIVE_DOCUMENTS_LIST_TEST_ID}>
        <StyledListWrapper>
          <>
            {areDocumentsMissing && !loading ? (
              <li>
                <NoDataPlaceholder
                  text="No documents are required, select “Complete” to move to next step"
                  data-test-id={NO_DOCUMENTS_PLACEHOLDER_TEST_ID}
                />
              </li>
            ) : (
              activeDocuments.map(
                ({
                  documentInfo,
                  availableActions,
                  isLoading,
                  actionsUiStores,
                  activeRestriction,
                  passedRestriction,
                }) => (
                  <DocumentCard
                    key={documentInfo.id}
                    isLoading={isLoading}
                    title={documentInfo.type}
                    restriction={activeRestriction}
                    passedRestriction={passedRestriction}
                    description={documentInfo.description}
                    uploader={documentInfo.uploader}
                    status={documentInfo.status}
                    availableActions={availableActions}
                    downloadUrl={documentInfo.templateLink}
                    warningModals={actionsUiStores.warningModalDelegates}
                    requestSignatureModalStore={actionsUiStores.requestSignatureModalStore || undefined}
                    editDocumentModalStore={actionsUiStores.editDocumentModalStore || undefined}
                    editTemplateModalStore={actionsUiStores.editTemplateModalStore || undefined}
                    disableMutativeActions={disableMutativeActions}
                  />
                ),
              )
            )}
          </>
          <OverlaySpinner display={loading} data-test-id={DOCUMENT_LIST_SPINNER_TEST_ID} />
        </StyledListWrapper>

        {!loading && inactiveDocuments.length > 0 && (
          <>
            <InactiveSectionStyled data-test-id={INACTIVE_DOCUMENTS_LIST_TEST_ID}>
              <InactiveListTitleStyled>Previously Uploaded Files</InactiveListTitleStyled>
              <InactiveListCaptionStyled>
                These files are inactive and displayed for informational purposes only.
              </InactiveListCaptionStyled>
            </InactiveSectionStyled>
            <StyledListWrapper>
              {inactiveDocuments.map(({ documentInfo, availableActions, isLoading, actionsUiStores }) => (
                <DocumentCard
                  key={documentInfo.id}
                  isLoading={isLoading}
                  title={documentInfo.type}
                  warningModals={actionsUiStores.warningModalDelegates}
                  description={documentInfo.description}
                  uploader={documentInfo.uploader}
                  status={DocumentStatus.INACTIVE}
                  availableActions={availableActions}
                  disableMutativeActions
                  passedRestriction
                />
              ))}
            </StyledListWrapper>
          </>
        )}
      </DocumentsSectionStyled>
    );
  },
);
